import { useEffect, useState } from "react";
import { ethers } from "ethers";

const BASE_CHAIN_ID = "0x2105";
const LOVE_ADDRESS = "0x16A7F8CfAD687A87183fCbd1dF7aF09dce05D357";
const LOVE_ABI = ["function transfer(address to, uint256 amount) returns (bool)"];

type Props = {
  faceId: string;
  handle: string;
  recipient?: string;
  verified?: boolean;
};

function shortAddress(value: string) {
  return `${value.slice(0, 7)}…${value.slice(-5)}`;
}

export default function LoveTransferPanel({ faceId, handle, recipient, verified }: Props) {
  const [amount, setAmount] = useState("1");
  const [status, setStatus] = useState<"idle" | "connecting" | "confirming" | "pending" | "success" | "error">("idle");
  const [message, setMessage] = useState("指挥舱一键直付，或连接钱包逐笔签名");
  const [txHash, setTxHash] = useState<string | null>(null);

  const canPay = Boolean(recipient && verified && ethers.isAddress(recipient));

  // 从指挥舱付款返回:#love_tx=<hash>
  useEffect(() => {
    const match = window.location.hash.match(/^#love_tx=(0x[0-9a-fA-F]{64})$/);
    if (match) {
      setTxHash(match[1]);
      setStatus("success");
      setMessage("指挥舱转账完成，LOVE 已送达");
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  // 免钱包:跳 u.jic.io 指挥舱,自动弹出预填转账窗,付完跳回本页
  const payViaCabin = () => {
    if (!canPay || !recipient) return;
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setStatus("error");
      setMessage("请输入大于 0 的 LOVE 数量");
      return;
    }
    const params = new URLSearchParams({
      pay_to: recipient,
      pay_amount: amount,
      pay_token: "LOVE",
      pay_note: `@${handle}`,
      pay_return: window.location.href.split("#")[0],
    });
    window.location.href = `https://u.jic.io/u?${params.toString()}`;
  };

  const sendLove = async () => {
    if (!canPay || !recipient) return;
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setStatus("error");
      setMessage("请输入大于 0 的 LOVE 数量");
      return;
    }
    const injected = window.ethereum;
    if (!injected) {
      setStatus("error");
      setMessage("没有检测到钱包，请用 TokenPocket、MetaMask 或其他钱包浏览器打开");
      return;
    }

    try {
      setTxHash(null);
      setStatus("connecting");
      setMessage("正在连接钱包…");
      await injected.request({ method: "eth_requestAccounts" });
      try {
        await injected.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: BASE_CHAIN_ID }],
        });
      } catch (switchError) {
        const code = (switchError as { code?: number }).code;
        if (code !== 4902) throw switchError;
        await injected.request({
          method: "wallet_addEthereumChain",
          params: [{
            chainId: BASE_CHAIN_ID,
            chainName: "Base",
            nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
            rpcUrls: ["https://mainnet.base.org"],
            blockExplorerUrls: ["https://basescan.org"],
          }],
        });
      }

      setStatus("confirming");
      setMessage(`请在钱包确认向 @${handle} 发送 ${amount} LOVE`);
      const provider = new ethers.BrowserProvider(injected as unknown as ethers.Eip1193Provider);
      const signer = await provider.getSigner();
      const contract = new ethers.Contract(LOVE_ADDRESS, LOVE_ABI, signer);
      const transaction = await contract.transfer(recipient, ethers.parseUnits(amount, 18));
      setTxHash(transaction.hash);
      setStatus("pending");
      setMessage("交易已提交，正在等待 Base 确认…");
      await transaction.wait();
      setStatus("success");
      setMessage(`${amount} LOVE 已传递给 @${handle}`);
    } catch (error) {
      const raw = error instanceof Error ? error.message : "交易未完成";
      const rejected = /rejected|denied|cancelled|canceled/i.test(raw);
      setStatus("error");
      setMessage(rejected ? "你已取消钱包签名，没有发生转账" : raw.slice(0, 180));
    }
  };

  return (
    <section className="face3-love-panel" aria-label="通过 Face ID 传递 LOVE">
      <div className="face3-love-recipient">
        <span className="face3-love-heart">♡</span>
        <div><small>FACE ID 已识别</small><strong>@{handle}</strong><em>{faceId}</em></div>
      </div>
      <div className={`face3-love-binding ${canPay ? "is-verified" : ""}`}>
        <small>收款账户</small>
        <strong>{canPay && recipient ? shortAddress(recipient) : "尚未绑定已验证 0x"}</strong>
        <span>{canPay ? "✓ 活体认领绑定" : "先由本人完成活体认领"}</span>
      </div>
      <label className="face3-love-amount">
        <small>发送数量</small>
        <div><input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} aria-label="LOVE 数量" /><span>LOVE</span></div>
      </label>
      <button type="button" className="face3-love-send" onClick={payViaCabin} disabled={!canPay}>
        ⚡ 指挥舱直付（免连钱包）
      </button>
      <button type="button" className="face3-love-send face3-love-send-wallet" onClick={sendLove} disabled={!canPay || ["connecting", "confirming", "pending"].includes(status)}>
        {status === "connecting" ? "连接钱包…" : status === "confirming" ? "等待签名…" : status === "pending" ? "Base 确认中…" : "用钱包发送 LOVE"}
      </button>
      <div className={`face3-love-status is-${status}`}><i />{message}</div>
      {txHash && <a className="face3-love-tx" href={`https://basescan.org/tx/${txHash}`} target="_blank" rel="noreferrer">在 BaseScan 查看交易 ↗</a>}
      <p>Base · A-LOVE NAV 份额合约 <a href={`https://basescan.org/token/${LOVE_ADDRESS}`} target="_blank" rel="noreferrer">{shortAddress(LOVE_ADDRESS)}</a>。脸用于找到收款人，不代替钱包签名。</p>
    </section>
  );
}
